import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useFetch } from "@/lib/content";
import { useSEO, serviceSchema } from "@/lib/seo";
import { Eyebrow, Reveal, ParallaxImage, CtaButton } from "@/components/site/Motion";
import RichText from "@/components/site/RichText";

export default function ServiceSeriesPage() {
  const { slug } = useParams();
  const { data: series, loading, error } = useFetch(`/services/${slug}`);
  const { data: all } = useFetch("/services");

  useSEO({
    title: series?.seo_title || (series ? `${series.title} — ${series.series_code} Series | RenB Solutions` : "RenB Solutions"),
    description: series?.seo_description || series?.excerpt,
    type: "website",
    path: `/services/${slug}`,
    image: series?.hero_image,
    jsonLd: series ? [serviceSchema(series)] : [],
  });

  if (loading)
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <p className="text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      </div>
    );

  if (error || !series)
    return (
      <div className="mx-auto max-w-[1440px] px-5 pb-24 pt-40 md:px-10">
        <h1 className="font-serif text-5xl">Series not found.</h1>
        <Link to="/services" className="mt-6 inline-block text-sm font-bold uppercase tracking-[0.14em] text-progress">
          ← All services
        </Link>
      </div>
    );

  const others = (all || []).filter((s) => s.slug !== slug).slice(0, 2);

  return (
    <div data-testid="service-series-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-24 md:pt-40 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow>{series.series_code} Series</Eyebrow>
            <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              {series.title}
            </h1>
          </div>
          <div className="lg:col-span-5 lg:self-end">
            <p className="text-base leading-relaxed text-obsidian/70 md:text-lg">{series.excerpt}</p>
            {series.method_line && (
              <p data-testid="series-method-line" className="mt-5 border-l-2 border-progress pl-4 text-[15px] leading-relaxed text-obsidian/80">
                {series.method_line}{" "}
                <Link to="/frameworks" className="font-bold text-obsidian underline-offset-4 hover:text-progress hover:underline">
                  About our frameworks
                </Link>
              </p>
            )}
            <div className="mt-8">
              <CtaButton testid="series-cta" href="/book">
                Discuss this program
              </CtaButton>
            </div>
          </div>
        </div>
        {series.hero_image && (
          <ParallaxImage src={series.hero_image} alt={series.title} className="mx-auto aspect-[21/9] max-w-[1440px]" />
        )}
      </section>

      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-32 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow>Overview</Eyebrow>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <RichText html={series.description} />
            </Reveal>
          </div>
        </div>
      </section>

      {series.programs?.length > 0 && (
        <section className="border-b border-ash">
          <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
            <Reveal>
              <Eyebrow>The program</Eyebrow>
              <h2 className="mt-8 font-serif text-4xl font-medium tracking-tight md:text-6xl">Sub-programs</h2>
            </Reveal>
            <div className="mt-14">
              {series.programs.map((p, i) => (
                <Reveal key={i} delay={Math.min(i * 0.04, 0.2)}>
                  <div className="grid gap-4 border-t border-ash py-8 md:grid-cols-12 md:gap-8 md:py-10" data-testid={`program-${i}`}>
                    <p className="font-serif text-3xl font-medium text-progress md:col-span-2 md:text-4xl">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <h3 className="font-serif text-2xl font-semibold tracking-tight md:col-span-4 md:text-3xl">{p.title}</h3>
                    <p className="text-[15px] leading-relaxed text-obsidian/65 md:col-span-6">{p.description}</p>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-ash" />
            </div>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
          <Eyebrow>Other series</Eyebrow>
          <div className="mt-10 grid gap-px border border-ash bg-ash md:grid-cols-2">
            {others.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                data-testid={`other-series-${s.slug}`}
                className="group flex items-center justify-between bg-paper p-8 transition-colors duration-300 hover:bg-obsidian md:p-12"
              >
                <div className="flex items-center gap-6">
                  <span className="font-serif text-6xl font-medium text-ash transition-colors group-hover:text-progress">{s.series_code}</span>
                  <div>
                    <h3 className="font-serif text-2xl font-medium tracking-tight transition-colors group-hover:text-paper">{s.title}</h3>
                    <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-slategray transition-colors group-hover:text-paper/50">
                      {s.series_code} Series
                    </p>
                  </div>
                </div>
                <ArrowUpRight size={20} className="transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-paper" />
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-obsidian hover:text-progress">
              <ArrowLeft size={16} /> All services
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
